import type { ButtonProps } from '@chakra-ui/react';
import { ConfirmButton } from '@ui/components/SwapDialog/ConfirmButton';
import { toaster } from '@ui/components/Toaster';
import { Token } from '@ui/config/token';
import { useBondingCurveTrade } from '@ui/hooks/bondingCurve/useBondingCurveTrade';
import {
  useAgentFunBalance,
  useTokenBalance,
} from '@ui/hooks/contracts/useTokenBalance';
import { useTokenConfig } from '@ui/hooks/contracts/useTokenConfig';
import { useAmountInputError } from '@ui/hooks/useAmountInputError';
import { Fraction } from 'bi-fraction';
import { useMemo } from 'react';

import { useBondingTradeDialog } from '../useBondingTradeDialog';
import { useBondingTradeForm } from './BondingTradeForm/useBondingTradeForm';

export const BondingTradeConfirmButton = (props: ButtonProps) => {
  const { title, isLoading, onClick, disabled } = useConfirmButton();
  const fromToken = useBondingTradeForm((s) => s.fromToken);
  const bondingTokenAddress = useBondingTradeDialog(
    (s) => s.bondingTokenAddress,
  );
  const enteredAmountStr = useBondingTradeForm((s) => s.enteredAmountStr);
  const error = useAmountInputError({
    fromToken: fromToken,
    tokenAddress: bondingTokenAddress,
    enteredAmountStr: enteredAmountStr,
  });

  return (
    <ConfirmButton
      title={title}
      isLoading={isLoading}
      disabled={disabled || !!error}
      {...props}
      onClick={onClick}
    />
  );
};
const useConfirmButton = () => {
  const balance = useAgentFunBalance().data;
  const fromToken = useBondingTradeForm((s) => s.fromToken);
  const amount = useBondingTradeForm((s) => s.enteredAmount);
  const { mutateAsync: tradeTokens, isLoading } = useBondingCurveTrade(
    fromToken === Token.Bonding ? 'sell' : 'buy',
  );
  const tokenAddress = useBondingTradeDialog((s) => s.bondingTokenAddress);
  const bondingTokenInfo = useTokenConfig({
    token: Token.Bonding,
    tokenAddress,
  });
  const title = useMemo(() => {
    const bondingTokenName = bondingTokenInfo?.symbol
      ? ` ${bondingTokenInfo.symbol}`
      : '';
    return (fromToken === Token.Bonding ? 'Sell' : 'Buy') + bondingTokenName;
  }, [bondingTokenInfo?.symbol, fromToken]);

  const bondingBalance = useTokenBalance({
    tokenAddress: fromToken === Token.Bonding ? tokenAddress : undefined,
  }).data;

  const fromBalance = fromToken === Token.Bonding ? bondingBalance : balance;

  const disabled = isLoading || !fromBalance || fromBalance.lte(Fraction.ZERO);
  const onClick = async () => {
    const isSell = fromToken === Token.Bonding;

    useBondingTradeDialog.setState({
      closable: false,
    });

    try {
      const result = await tradeTokens({ tokenAddress, amount });
      toaster.create({
        type: 'success',
        title: isSell
          ? '(＾▽＾) Sold successfully'
          : '(＾▽＾) Bought successfully',
        description: isSell
          ? `You have successfully sold ${bondingTokenInfo?.symbol ?? ''}!`
          : `You have successfully bought ${bondingTokenInfo?.symbol ?? ''}!`,
        meta: {
          txHash: result.receipt?.hash,
        },
      });
      useBondingTradeDialog.getState().close();
    } catch (e) {
      toaster.create({
        type: 'error',
        title: '(〒︿〒) Something went wrong!',
        description: isSell
          ? `Failed to sell ${bondingTokenInfo?.symbol ?? ''}, let’s try again!`
          : `Failed to buy ${bondingTokenInfo?.symbol ?? ''}, let’s try again!`,
      });
    } finally {
      useBondingTradeDialog.setState({
        closable: true,
      });
    }
  };

  return {
    title,
    isLoading,
    onClick,
    disabled,
  };
};
